import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

interface Album {
  _id: string;
  title: string;
  slug: string;
  releaseDate: string;
  genre: string;
}
interface Artist {
  _id: string;
  stageName: string;
}

export default function Albums() {
  const qc = useQueryClient();
  const { data: artists } = useQuery<{ items: Artist[] }>({
    queryKey: ['artists', 'all'],
    queryFn: () => api('/api/v1/artists?limit=200'),
  });
  const { data: albumsData } = useQuery<{ items: Album[] }>({
    queryKey: ['admin', 'albums'],
    queryFn: async () => {
      // No /admin/albums list yet — paginate via artists
      const all: Album[] = [];
      for (const a of artists?.items ?? []) {
        const r = await api<{ artist: unknown; albums: Album[]; topTracks: unknown[] }>(
          `/api/v1/artists/${(a as Artist & { slug: string }).slug ?? ''}`,
        ).catch(() => null);
        if (r) all.push(...r.albums);
      }
      return { items: all };
    },
    enabled: Boolean(artists?.items),
  });

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [artistId, setArtistId] = useState('');
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [genre, setGenre] = useState('rumba');

  const create = useMutation({
    mutationFn: () =>
      api('/api/v1/admin/albums', {
        method: 'POST',
        body: JSON.stringify({ title, artistId, releaseDate, genre }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'albums'] });
      setOpen(false);
      setTitle('');
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Albums</h1>
        <button onClick={() => setOpen((v) => !v)} className="bg-accent text-bg font-semibold px-4 py-2 rounded-md">
          {open ? 'Cancel' : '+ New album'}
        </button>
      </div>
      {open ? (
        <form onSubmit={submit} className="bg-surface border border-border rounded-xl p-6 mb-6 grid grid-cols-2 gap-3">
          <select
            value={artistId}
            onChange={(e) => setArtistId(e.target.value)}
            className="bg-bg border border-border rounded-md px-3 py-2"
          >
            <option value="">Select artist</option>
            {artists?.items?.map((a) => (
              <option key={a._id} value={a._id}>
                {a.stageName}
              </option>
            ))}
          </select>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Album title"
            className="bg-bg border border-border rounded-md px-3 py-2"
          />
          <input
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            className="bg-bg border border-border rounded-md px-3 py-2"
          />
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="bg-bg border border-border rounded-md px-3 py-2"
          >
            {['rumba', 'coupe-decale', 'afrobeat', 'soukous', 'ndombolo'].map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
          <button
            className="col-span-2 bg-accent text-bg px-4 py-2 rounded-md font-semibold"
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </form>
      ) : null}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface2 text-left text-text2">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Genre</th>
              <th className="px-4 py-3">Release date</th>
            </tr>
          </thead>
          <tbody>
            {(albumsData?.items ?? []).map((a) => (
              <tr key={a._id} className="border-t border-border">
                <td className="px-4 py-3">{a.title}</td>
                <td className="px-4 py-3 text-accent">{a.genre}</td>
                <td className="px-4 py-3">{new Date(a.releaseDate).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
